import * as Cesium from 'cesium';
import { eciToGeodetic, gstime, propagate, twoline2satrec } from 'satellite.js';
import type { SatRec } from 'satellite.js';
import {
  PASS_ARC_LABEL_FONT,
  PASS_ARC_LABEL_PIXEL_OFFSET_Y,
  PASS_ARC_SUB_SAMPLES,
  PASS_ARC_WIDTH_PX,
  formatPeakLabel,
  passArcTimestamps,
} from './passArcMath';

/**
 * Phase 7 chunk 3B — pass-arc layer for sky view.
 *
 * Given a list of `(norad, label, rise_at, peak_at, set_at) + TLE`
 * records, propagates each pass to ~30 sub-samples between rise and
 * set, then renders one polyline per pass (the satellite's
 * trajectory across the local sky from the observer's POV) + one
 * label at the peak point with `"{name} · {peak HH:MM}"`.
 *
 * Why client-side propagation instead of asking the server for the
 * polyline points: the server returns rise/peak/set + max-elevation
 * tuples (cheap), the client has the TLEs already loaded
 * (PointPrimitiveLayer.tleByNorad), and satellite.js propagation
 * for ~30 sub-samples × ~7 marquee passes per day = ~600 calls is
 * well under 50ms total. Keeping the polyline geometry client-side
 * also lets a re-propagation on time-scrub be trivial.
 *
 * Lifecycle:
 *  - `attach(viewer)` adds the collection placeholders to the scene.
 *  - `setPasses(records[])` clears existing arcs + builds new ones
 *    from the records. Idempotent; safe to call repeatedly.
 *  - `detach()` removes both collections. Safe after viewer destroy.
 */

export interface PassArcRecord {
  norad: number;
  label: string;
  rise_at: string;
  peak_at: string;
  set_at: string;
  max_elevation_deg: number;
  tle: { line1: string; line2: string };
}

/** Tinted color per pass. Could vary per-NORAD in a future polish
 *  pass; chunk 3B uses one accent-cyan for everything. */
const PASS_ARC_COLOR = Cesium.Color.fromCssColorString('#00d9ff').withAlpha(0.7);

const PASS_ARC_LABEL_FILL = Cesium.Color.fromCssColorString('#00d9ff').withAlpha(0.95);

export class PassArcLayer {
  private viewer: Cesium.Viewer | null = null;
  private polylineCollection: Cesium.PolylineCollection | null = null;
  private labelCollection: Cesium.LabelCollection | null = null;
  /** Phase 7 chunk 3B — marquee passes (curated roster). */
  private marqueeRecords: readonly PassArcRecord[] = [];
  /** Phase 7 chunk 3C — passes for the currently-selected satellite.
   *  Deduped against marqueeRecords by (norad, peak_at) so a selection
   *  that is also a marquee sat doesn't render the same arc twice. */
  private selectedRecords: readonly PassArcRecord[] = [];

  /** Attach the collection placeholders to the scene. Idempotent. */
  attach(viewer: Cesium.Viewer): void {
    if (this.viewer !== null) return;
    this.viewer = viewer;
    this.polylineCollection = viewer.scene.primitives.add(
      new Cesium.PolylineCollection(),
    ) as Cesium.PolylineCollection;
    this.labelCollection = viewer.scene.primitives.add(
      new Cesium.LabelCollection(),
    ) as Cesium.LabelCollection;
  }

  /** Replace the marquee-roster arc set. Selected slot is untouched. */
  setMarquee(records: readonly PassArcRecord[]): void {
    this.marqueeRecords = records;
    this.renderAll();
  }

  /** Replace the selected-satellite arc set. Marquee slot is untouched.
   *  Pass an empty array to clear (e.g. on deselect). */
  setSelected(records: readonly PassArcRecord[]): void {
    this.selectedRecords = records;
    this.renderAll();
  }

  /**
   * Replace ALL rendered arcs in one call — equivalent to
   * setMarquee(records) + setSelected([]). Kept for backward
   * compatibility with chunk-3B callers + as a "blank the layer"
   * shortcut.
   */
  setPasses(records: readonly PassArcRecord[]): void {
    this.marqueeRecords = records;
    this.selectedRecords = [];
    this.renderAll();
  }

  /**
   * Post-Phase-11 — show/hide the entire layer. Cheap: just toggles
   * the `.show` flag on both Cesium collections; no geometry rebuild.
   * Driven by the §overlays `passArcs` toggle in `<sat-overlays-menu>`.
   * Idempotent and safe to call before {@link attach}.
   */
  setVisible(visible: boolean): void {
    if (this.polylineCollection !== null) this.polylineCollection.show = visible;
    if (this.labelCollection !== null)    this.labelCollection.show    = visible;
  }

  private renderAll(): void {
    if (this.viewer === null || this.polylineCollection === null || this.labelCollection === null) {
      return;
    }
    this.polylineCollection.removeAll();
    this.labelCollection.removeAll();

    // Dedupe by (norad, peak_at) so a selected-sat-that-is-also-marquee
    // doesn't render its arc twice with stacked alpha.
    const seen = new Set<string>();
    const unionRecords: PassArcRecord[] = [];
    for (const record of this.marqueeRecords) {
      const key = `${record.norad} ${record.peak_at}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unionRecords.push(record);
    }
    for (const record of this.selectedRecords) {
      const key = `${record.norad} ${record.peak_at}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unionRecords.push(record);
    }

    for (const record of unionRecords) {
      this.renderOne(record);
    }
  }

  /** Tear down both collections. Safe after viewer destroy. */
  detach(): void {
    if (this.viewer === null) return;
    if (!this.viewer.isDestroyed()) {
      if (this.polylineCollection !== null) {
        this.viewer.scene.primitives.remove(this.polylineCollection);
      }
      if (this.labelCollection !== null) {
        this.viewer.scene.primitives.remove(this.labelCollection);
      }
    }
    this.polylineCollection = null;
    this.labelCollection = null;
    this.viewer = null;
    this.marqueeRecords = [];
    this.selectedRecords = [];
  }

  /** True iff currently attached. */
  isAttached(): boolean {
    return this.viewer !== null;
  }

  // ─── Internals ───────────────────────────────────────────────────────

  private renderOne(record: PassArcRecord): void {
    if (this.polylineCollection === null || this.labelCollection === null) return;

    const riseMs = Date.parse(record.rise_at);
    const setMs = Date.parse(record.set_at);
    if (!Number.isFinite(riseMs) || !Number.isFinite(setMs) || setMs <= riseMs) return;

    let satrec: SatRec;
    try {
      satrec = twoline2satrec(record.tle.line1, record.tle.line2);
    } catch {
      return;                                                                 // unparseable TLE — skip silently
    }

    const timestamps = passArcTimestamps(riseMs, setMs, PASS_ARC_SUB_SAMPLES);
    const positions: Cesium.Cartesian3[] = [];
    for (const t of timestamps) {
      const pt = this.propagateToCartesian(satrec, t);
      if (pt !== null) positions.push(pt);
    }
    if (positions.length < 2) return;                                         // not enough points to draw a line

    this.polylineCollection.add({
      positions,
      width: PASS_ARC_WIDTH_PX,
      material: Cesium.Material.fromType(Cesium.Material.ColorType, {
        color: PASS_ARC_COLOR,
      }),
    });

    // Peak label — propagate at peak_at specifically so the label sits
    // at the satellite's exact closest-approach position.
    const peakMs = Date.parse(record.peak_at);
    const peakPt = Number.isFinite(peakMs) ? this.propagateToCartesian(satrec, new Date(peakMs)) : null;
    if (peakPt !== null) {
      this.labelCollection.add({
        position: peakPt,
        text: formatPeakLabel(record.peak_at, record.label),
        font: PASS_ARC_LABEL_FONT,
        fillColor: PASS_ARC_LABEL_FILL,
        outlineColor: Cesium.Color.BLACK.withAlpha(0.6),
        outlineWidth: 2,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        pixelOffset: new Cesium.Cartesian2(0, PASS_ARC_LABEL_PIXEL_OFFSET_Y),
      });
    }
  }

  /** Propagate a satrec at a date and return the geodetic→ECEF
   *  Cartesian3, or null if propagation fails. */
  private propagateToCartesian(satrec: SatRec, when: Date): Cesium.Cartesian3 | null {
    const propagated = propagate(satrec, when);
    if (typeof propagated === 'boolean' || !propagated.position) {
      return null;
    }
    const positionEci = propagated.position as Exclude<typeof propagated.position, true>;
    const gmst = gstime(when);
    const geo = eciToGeodetic(positionEci, gmst);
    const lonDeg = (geo.longitude * 180) / Math.PI;
    const latDeg = (geo.latitude * 180) / Math.PI;
    const altMeters = geo.height * 1000;        // satellite.js emits km
    return Cesium.Cartesian3.fromDegrees(lonDeg, latDeg, altMeters);
  }
}
